import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { PracticeChallenge } from "@/components/PracticeChallenge";
import { Insight } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import {
  Tip, KeyIdea, Pull, Steps, Step, Vocab, Term, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation10() {
  const { lesson, course, accent } = findLesson("piano-foundation", "10");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>The fifth chord = <strong>root + fifth + octave</strong>. Three notes, no third — works under almost any song, major or minor.</Highlight>
        <Highlight>Two right-hand shapes for the same chord: <strong>D-A-D</strong> (root-shape) and <strong>A-D-A</strong> (inversion). Pick whichever sits closer to where your hand already is.</Highlight>
        <Highlight>Guitar players call this a "power chord". Keep yours <strong>around the middle of the keyboard</strong> — too high annoys the singer, too low fights the bass player.</Highlight>
        <Highlight>The left-hand bass walk is <strong>6 - 4 - 1 - 5</strong> (Dha-Ma-Sa-Pa) — the progression behind "Wake Me Up", every Adele song, plenty of Coldplay.</Highlight>
        <Highlight>Hand ratio for this exercise: <strong>1:4</strong> — left bass once, right chord four times. A great ratio to practise on your body before the piano.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="Your first chord" title="Just three notes — root, fifth, octave" accent={accent.hex}>
        <p>
          The fifth chord is the simplest chord on the piano that still sounds like a chord.
          To form it, you need just two pieces of information from any major scale: the
          <strong> root</strong> and the <strong>fifth</strong> above it. Then you double the
          root one octave up to thicken the sound.
        </p>

        <Vocab>
          <Term name="Fifth (5th)" def="the fifth note of the major scale, counting up from the root. In D major, the fifth is A." />
          <Term name="Power chord" def="guitarist name for the same shape — root + 5th + octave. They play it in the deep end; pianists usually play higher up to avoid clutter." />
          <Term name="Inversion" def="reordering the chord notes. Same three pitches, different bottom note." />
          <Term name="Hand ratio" def="how many right-hand hits you play per single left-hand bass — Lesson 15 covers this in depth. Here we just use 1:4." />
        </Vocab>

        <KeyIdea>
          The fifth chord has no third — and that's why it works under both major and minor
          progressions. There's nothing in it to clash with whatever the singer or melody is
          doing. It's the most "neutral" chord on the instrument.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="The two shapes" title="D-A-D and its flip A-D-A" accent={accent.hex}>
        <p>
          The demonstration is in D major. The fifth chord here uses three notes:
          <strong> D, A, D</strong>. Two ways your right hand can hold them:
        </p>

        <ul className="space-y-1 text-[14px]">
          <li><strong>Shape 1 · D-A-D</strong> — root on the bottom (D), fifth in the middle (A), octave on top (D). This is the home shape.</li>
          <li><strong>Shape 2 · A-D-A</strong> — fifth on the bottom (A), root in the middle (D), octave of the fifth on top (A). The inversion.</li>
        </ul>

        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "D4", color: "#22c55e", label: "D" },
            { pitch: "A4", color: "#3b82f6", label: "A" },
            { pitch: "D5", color: "#22c55e", label: "D" },
          ]}
          height={170}
        />

        <PracticeChallenge
          courseSlug="piano-foundation"
          order="10"
          id="fifth-chord"
          mode="chord"
          title="The fifth chord · D-A-D"
          prompt="Play the D fifth chord — root, fifth, octave — all three notes together: D, A, D."
          notes={["D4", "A4", "D5"]}
          startOctave={3}
          octaves={3}
        />

        <Tip>
          Park the chord <strong>somewhere in the middle of the keyboard</strong> — around
          middle C is a great spot. Too high and the vocalist gets buried under the chord;
          too low and you compete with the bass player. The middle-keyboard zone leaves room
          for everyone in the band.
        </Tip>
      </Insight>

      <Insight eyebrow="The bass movement" title="6 - 4 - 1 - 5 in the left hand" accent={accent.hex}>
        <p>
          With the right hand on the D-A-D fifth chord, the left hand provides the harmonic
          motion. The chosen progression: <strong>6 - 4 - 1 - 5</strong>. In D major
          (and in swaras: Dha, Ma, Sa, Pa) those scale degrees are:
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          6 = B (Dha) · 4 = G (Ma) · 1 = D (Sa) · 5 = A (Pa)
        </p>

        <Pull>
          For example, Wake Me Up by Avicii. You must have heard that. It's an awesome
          song. That's your 6-4-1-5.
        </Pull>

        <p>
          You don't shift the LH that fast. The right hand plays the fifth chord{" "}
          <strong>four times</strong>, then the left hand changes to the next bass note. That's
          the <strong>1:4 hand ratio</strong> introduced here.
        </p>
      </Insight>

      <Insight eyebrow="Practise on your body first" title="Tap the 1:4 ratio before the piano touches it" accent={accent.hex}>
        <p>
          Before the piano, get the body comfortable with the ratio. Right hand taps four
          beats on a thigh; left hand taps once on the chest (or the other thigh) per cycle —
          the chest gives a nicer bass-thump simulation.
        </p>

        <Steps>
          <Step>
            Tap right-hand four-on-the-floor on your thigh: <em>1 · 2 · 3 · 4</em>.
          </Step>
          <Step>
            Add the left-hand bass — one thump on beat 1 of every group of four. <em>BUMP-2-3-4</em>.
          </Step>
          <Step>
            Once that's locked, transfer to the piano. RH plays the D fifth chord four times;
            LH plays D once.
          </Step>
          <Step>
            Then move the LH through the bass walk: B (4× chord) → G (4× chord) → D (4× chord) → A (4× chord) → repeat.
          </Step>
        </Steps>

        <Tip>
          If your hand can't reach the octave for the LH, just play single notes — a clean
          single bass note in time beats a strained octave that drags. Add the octave later
          once your hand stretches comfortably.
        </Tip>
      </Insight>

      <Insight eyebrow="Two progressions, one song template" title="6-4-1-5 and 1-5-6-4" accent={accent.hex}>
        <p>
          Once 6-4-1-5 is solid, jumble the same four notes into{" "}
          <strong>1 - 5 - 6 - 4</strong>: D, A, B, G. Same chord shape on top; new bass
          movement underneath. That's the progression behind a different ocean of pop —
          countless Adele tunes, plenty of Green Day, plenty of Coldplay.
        </p>

        <Pull>
          It's pretty much just the fifth chord which is happening here. You know, you
          can do every Adele song, can do every Green Day song, Coldplay song, or you
          could make up your own songs, which I'll highly encourage you to do.
        </Pull>
      </Insight>

      <Insight eyebrow="Then move keys" title="Same exercise, different scale" accent={accent.hex}>
        <p>
          To find your singing voice, you'll quickly outgrow D major. Move the same exercise
          to C major: 1=C, 5=G, 6=A, 4=F. Then to other scales as your voice prefers. The
          shape of the right-hand fifth chord doesn't change — only the keys it sits on.
        </p>

        <p>
          You can also speed up the chord rate by switching the ratio. <strong>1:2</strong>{" "}
          means one bass note for every two chord hits — that means the bass changes twice as
          fast. Same exercise, more energetic feel.
        </p>

        <Pull>
          I would say enjoy the process. Don't look at any kind of results when you
          start off your journey with the instrument. Just enjoy the journey or the
          process as they say and there will come a point where things just skyrocket.
          You will feel that you can literally play anything.
        </Pull>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-11"
        label="Lesson 11 · Hit the Road Jack"
        why="With the fifth-chord template under your hand, the next lesson applies it to a real Ray Charles song — same kind of left-hand bass walk, this time in C minor."
      />
    </LessonShell>
  );
}
